const ATTACHMENT_HOST = 's3-eu-west-1.amazonaws.com';
const ATTACHMENT_PREFIX = '/bigchange/Attachment/';
const MAX_BYTES = 30 * 1024 * 1024;
const SESSION_URL = 'https://api.vh3connect.io/api:G6skVfcT/auth/me';
const SESSION_TIMEOUT_MS = 8_000;
const UPSTREAM_TIMEOUT_MS = 20_000;
const ALLOWED_TYPES = new Set([
  'application/pdf',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/** Public BigChange worksheet files. No other host is fetched. */
export function bigChangeAttachmentUrl(value: string): URL | null {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  if (parsed.username || parsed.password) return null;
  if (parsed.hostname !== ATTACHMENT_HOST) return null;
  if (!parsed.pathname.startsWith(ATTACHMENT_PREFIX)) return null;
  return parsed;
}

/** Keeps spaces and parentheses. Drops quotes, slashes, and control characters. */
export function safeFilename(value: string | null): string | null {
  const name = Array.from(value ?? '')
    .filter(character => {
      const code = character.charCodeAt(0);
      return code > 31 && code !== 127 && !'"\\/'.includes(character);
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, 120)
    .trim();
  if (!name || name === '.' || name === '..') return null;
  return name;
}

/**
 * `filename=` is ASCII, because Node rejects header characters above U+00FF.
 * `filename*` carries the real name (RFC 5987).
 */
export function contentDisposition(filename: string) {
  const quoted = Array.from(filename, character => {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code > 0x7e) return '_';
    if (character === '\\' || character === '"') return `\\${character}`;
    return character;
  }).join('');
  const encoded = encodeURIComponent(filename).replace(
    /[!'()*]/g,
    character => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
  );
  return `attachment; filename="${quoted}"; filename*=UTF-8''${encoded}`;
}

const bearerToken = (authorization: string | null) => {
  const match = /^Bearer\s+(\S+)$/.exec(authorization?.trim() ?? '');
  const token = match?.[1] ?? '';
  if (!token || token.length > 4096) return null;
  return token;
};

const withTimeout = async <T>(
  timeoutMs: number,
  run: (signal: AbortSignal) => Promise<T>
) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
};

/** A valid Connect session. This does not prove the file belongs to that tenant. */
const requireSession = async (
  authorization: string | null
): Promise<Response | null> => {
  const token = bearerToken(authorization);
  if (!token) return new Response('Sign in required', { status: 401 });

  try {
    const response = await withTimeout(SESSION_TIMEOUT_MS, signal =>
      fetch(SESSION_URL, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        redirect: 'manual',
        signal,
      })
    );
    await response.body?.cancel();
    if (response.status === 200) return null;
    if (response.status === 401 || response.status === 403) {
      return new Response('Sign in required', { status: 401 });
    }
    return new Response('Could not check the session', { status: 503 });
  } catch {
    return new Response('Could not check the session', { status: 503 });
  }
};

const publicType = (value: string) => {
  const base = value.split(';')[0]?.trim().toLowerCase() ?? '';
  return ALLOWED_TYPES.has(base) ? base : 'application/octet-stream';
};

const bytesOf = async (body: ReadableStream<Uint8Array>) => {
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > MAX_BYTES) {
      await reader.cancel();
      return null;
    }
    chunks.push(value);
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
};

const isErrorBody = (bytes: Uint8Array, type: string) => {
  if (type.startsWith('text/')) return true;
  const head = new TextDecoder().decode(bytes.slice(0, 80)).trimStart();
  return head.startsWith('<Error') || head.startsWith('<?xml');
};

/**
 * Same-origin stand-in for a BigChange file.
 * The browser cannot fetch that host: a request with Origin is rejected,
 * and a direct link then replaces this page.
 */
export async function attachmentFileResponse(
  requestUrl: URL,
  authorization: string | null
): Promise<Response> {
  const denied = await requireSession(authorization);
  if (denied) return denied;

  const filename = safeFilename(requestUrl.searchParams.get('filename'));
  const target = bigChangeAttachmentUrl(
    requestUrl.searchParams.get('url') ?? ''
  );
  if (!filename || !target) {
    return new Response('Unsupported file', { status: 400 });
  }

  let upstreamType = 'application/octet-stream';
  let bytes: Uint8Array | null;
  try {
    const loaded = await withTimeout(UPSTREAM_TIMEOUT_MS, async signal => {
      const upstream = await fetch(target.toString(), {
        redirect: 'manual',
        signal,
      });
      if (!upstream.ok || !upstream.body || upstream.status >= 300) return null;
      return {
        type:
          upstream.headers.get('content-type') ?? 'application/octet-stream',
        bytes: await bytesOf(upstream.body),
      };
    });
    if (!loaded)
      return new Response('Could not save this file', { status: 502 });
    upstreamType = loaded.type;
    bytes = loaded.bytes;
  } catch {
    return new Response('Could not save this file', { status: 502 });
  }
  if (!bytes) {
    return new Response('File is too large', { status: 413 });
  }
  if (isErrorBody(bytes, upstreamType)) {
    return new Response('Could not save this file', { status: 502 });
  }

  const type = publicType(upstreamType);
  return new Response(new Blob([bytes as unknown as BlobPart], { type }), {
    status: 200,
    headers: {
      'content-type': type,
      'content-length': String(bytes.byteLength),
      'content-disposition': contentDisposition(filename),
      'cache-control': 'private, no-store',
      'x-content-type-options': 'nosniff',
    },
  });
}

export function attachmentDownloadPath(url: string, filename: string) {
  const params = new URLSearchParams({ url, filename });
  return `/api/attachment-file?${params.toString()}`;
}
